import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Banner,
  Button,
  Flex,
  SettingsRow,
  SettingsSection,
  Spinner,
  Switch,
  Text,
  TextArea,
  TextField,
} from '@chatsdet/ui';
import { useSetAreaDisabled } from '../../../../../features/external-tests/useExternalRunActions';
import type { SuiteExternalRunner } from '../../../../../features/test-suites';
import { parseShardDirectories } from '../../../../../features/test-suites/areaPaths';
import { usePreviewExternalRunnerAreas } from '../../../../../features/test-suites/useExternalRunner';
import { useDebouncedValue } from '../../../../../hooks/useDebouncedValue';
import { ApiError } from '../../../../../lib/api-error';
import { AreaSettingsDialog, AreaSettingsIndicators } from './AreaSettingsDialog';

/** Last path segment, for the row's short label. The full path shows below it. */
function areaLabel(path: string): string {
  const segments = path.split('/').filter(Boolean);
  return segments[segments.length - 1] ?? path;
}

function sameStringSet(a: string[], b: string[]): boolean {
  return JSON.stringify([...a].sort()) === JSON.stringify([...b].sort());
}

function resolutionErrorDetails(error: unknown): { reason: string | null; paths: string[] } | null {
  if (!(error instanceof ApiError) || error.code !== 'EXTERNAL_AREAS_RESOLUTION_INVALID') {
    return null;
  }
  const reason = typeof error.details?.reason === 'string' ? error.details.reason : null;
  const rawPaths = error.details?.paths;
  const paths = Array.isArray(rawPaths)
    ? rawPaths.filter((path): path is string => typeof path === 'string')
    : [];
  return { reason, paths };
}

/** Shared inline-code styling used throughout settings help text. */
function Code({ children }: { children: React.ReactNode }) {
  return <code className="bg-muted px-1 rounded text-xs">{children}</code>;
}

export interface AreasSectionProps {
  suiteId: string;
  suiteName: string;
  runner: SuiteExternalRunner;
  /** One directory (or glob pattern) per line — controlled by the page's form state, saved with the rest of it. */
  value: string;
  onChange: (value: string) => void;
  /** Raw "Generated prefix" input text — empty string means no prefix. */
  generatedPrefix: string;
  onGeneratedPrefixChange: (value: string) => void;
}

/**
 * Directory areas: split a suite's tests into directories (or glob patterns
 * resolved against the repository) that run as separate parallel parts of
 * each run.
 *
 * @remarks Two independent edits live here, on purpose. The textarea/prefix
 * (which directories exist) is customer-editable configuration and rides the
 * page's normal Save flow, same as environment mapping. The on/off switches
 * per area are immediate — `disabledAreas` is a server-side exclusion set a
 * customer can flip mid-review without committing the rest of an
 * in-progress edit, and an area removed from the textarea without ever being
 * re-enabled should not quietly vanish, hence the "No longer configured"
 * list below.
 *
 * A saved line may be a glob (`.features-gen/bdd/features/*`), so the set of
 * concrete area paths it resolves to can only be known by asking the server
 * — the switch rows are therefore built from the **preview of the saved
 * config** (`usePreviewExternalRunnerAreas` over `runner.shardDirectories` +
 * `runner.areaGeneratedPrefix`), never from the raw saved lines and never
 * from the unsaved textarea text. While the textarea or prefix has unsaved
 * changes, we instead show a live (debounced) preview of the *edited*
 * config with no switches — an unsaved pattern has no row for the server to
 * toggle yet.
 */
export function AreasSection({
  suiteId,
  suiteName,
  runner,
  value,
  onChange,
  generatedPrefix,
  onGeneratedPrefixChange,
}: AreasSectionProps) {
  const queryClient = useQueryClient();
  const setAreaDisabled = useSetAreaDisabled();
  const [toggleError, setToggleError] = React.useState<string | null>(null);
  const [pendingPath, setPendingPath] = React.useState<string | null>(null);

  const savedPaths = React.useMemo(() => runner.shardDirectories ?? [], [runner.shardDirectories]);
  const savedPrefix = runner.areaGeneratedPrefix;
  const unsavedPaths = React.useMemo(() => parseShardDirectories(value) ?? [], [value]);
  const unsavedPrefix = generatedPrefix.trim() || null;
  const hasUnsavedChanges =
    !sameStringSet(unsavedPaths, savedPaths) || unsavedPrefix !== savedPrefix;

  // Debounce the edited config before it feeds the preview query, so typing
  // doesn't fire a resolution request (which may call GitHub) per keystroke.
  const debouncedUnsavedPaths = useDebouncedValue(unsavedPaths, 500);
  const debouncedUnsavedPrefix = useDebouncedValue(unsavedPrefix, 500);

  // Always resolved: the "No longer configured" list and (when unchanged)
  // the switches both need the SAVED config's resolved area list.
  const savedPreview = usePreviewExternalRunnerAreas(suiteId, savedPaths, savedPrefix, {
    enabled: savedPaths.length > 0,
  });
  // Only resolved while there's something unsaved to preview, and only once
  // typing has settled.
  const editedPreview = usePreviewExternalRunnerAreas(
    suiteId,
    debouncedUnsavedPaths,
    debouncedUnsavedPrefix,
    { enabled: hasUnsavedChanges && debouncedUnsavedPaths.length > 0 }
  );

  const activePreview = hasUnsavedChanges ? editedPreview : savedPreview;
  const effectivePaths = hasUnsavedChanges ? unsavedPaths : savedPaths;

  const resolvedSavedPathSet = React.useMemo(
    () => new Set((savedPreview.data?.areas ?? []).map((area) => area.path)),
    [savedPreview.data]
  );
  // Only meaningful once the saved config has actually resolved — before
  // that, every disabled area would look "no longer configured".
  const staleDisabled = savedPreview.data
    ? runner.disabledAreas.filter((path) => !resolvedSavedPathSet.has(path))
    : [];

  const toggle = async (path: string, disabled: boolean) => {
    setToggleError(null);
    setPendingPath(path);
    try {
      await setAreaDisabled.mutateAsync({ suiteId, path, disabled });
      void queryClient.invalidateQueries({
        queryKey: ['external-runner-areas-preview', suiteId],
      });
    } catch (err) {
      setToggleError(err instanceof Error ? err.message : 'Failed to update this area');
    } finally {
      setPendingPath(null);
    }
  };

  const switchAreas = hasUnsavedChanges ? [] : (savedPreview.data?.areas ?? []);
  const previewOnlyAreas = hasUnsavedChanges ? (editedPreview.data?.areas ?? []) : [];

  return (
    <SettingsSection
      title="Areas"
      description="Split this suite's tests into directories that run as separate parallel parts of each run."
    >
      <TextArea
        label="Area directories"
        description={
          <>
            One repository-relative directory or glob pattern per line; each line runs as its own
            area. <Code>*</Code>, <Code>?</Code>, and <Code>[abc]</Code> are allowed only in the
            last path segment — <Code>**</Code> and <Code>{'{a,b}'}</Code> aren't supported.
            Patterns are matched against your repository; the generated prefix below (e.g.{' '}
            <Code>.features-gen/</Code>) is stripped from glob patterns before matching and added
            back to each match; literal paths are used as written.
            Uploaded-bundle suites accept plain paths only. Leave empty to run the whole suite as
            a single area.
          </>
        }
        value={value}
        onChange={onChange}
        rows={4}
      />

      <TextField
        label="Generated prefix"
        description="Optional. Stripped from glob patterns before matching and restored only to glob matches."
        placeholder=".features-gen/"
        value={generatedPrefix}
        onChange={onGeneratedPrefixChange}
      />

      {activePreview.isLoading && (
        <Flex align="center" gap="xs">
          <Spinner size="sm" />
          <Text size="xs" className="text-muted-foreground">
            Resolving areas…
          </Text>
        </Flex>
      )}

      {activePreview.isError && (
        <Banner variant="error">
          <Text size="sm">
            {activePreview.error instanceof Error
              ? activePreview.error.message
              : 'Failed to preview these areas'}
          </Text>
          {(() => {
            const details = resolutionErrorDetails(activePreview.error);
            if (!details) return null;
            return (
              <Flex direction="column" gap="xs">
                {details.reason && <Text size="sm">Reason: {details.reason}</Text>}
                {details.paths.length > 0 && (
                  <Text size="sm">Paths: {details.paths.join(', ')}</Text>
                )}
              </Flex>
            );
          })()}
        </Banner>
      )}

      {activePreview.data?.commitSha && (
        <Text size="xs" className="text-muted-foreground">
          Resolved at <code>{activePreview.data.commitSha.slice(0, 7)}</code>
        </Text>
      )}

      {(activePreview.data?.unmatchedPatterns.length ?? 0) > 0 && (
        <Banner variant="warning">
          <Flex direction="column" gap="xs">
            {activePreview.data?.unmatchedPatterns.map((pattern) => (
              <Text size="sm" key={pattern}>
                No directories match <code>{pattern}</code>
              </Text>
            ))}
          </Flex>
        </Banner>
      )}

      {toggleError && (
        <Text size="sm" className="text-destructive">
          {toggleError}
        </Text>
      )}

      {hasUnsavedChanges && (
        <Flex direction="column" gap="xs">
          <Text size="xs" className="text-muted-foreground">
            Preview — save to apply
          </Text>
          {previewOnlyAreas.map((area) => (
            <SettingsRow key={area.path} title={area.label} description={area.path}>
              {null}
            </SettingsRow>
          ))}
        </Flex>
      )}

      {!hasUnsavedChanges && switchAreas.length > 0 && (
        <Flex direction="column" gap="xs">
          {switchAreas.map((area) => {
            const settings = runner.areaSettings[area.path];
            return (
              <Flex
                key={area.path}
                align="center"
                justify="between"
                gap="md"
                className="-mx-3 rounded-lg px-3 py-3 transition-colors hover:bg-muted/50"
              >
                <div className="min-w-0 flex-1">
                  <Flex align="center" gap="sm">
                    <Text weight="medium">{area.label}</Text>
                    <AreaSettingsIndicators settings={settings} />
                  </Flex>
                  <Text size="sm" className="mt-1 text-muted-foreground">
                    {area.path}
                  </Text>
                </div>
                <Flex align="center" gap="sm" className="shrink-0">
                  <AreaSettingsDialog
                    suiteId={suiteId}
                    suiteName={suiteName}
                    area={area}
                    settings={settings}
                    disabled={area.disabled}
                  />
                  <Switch
                    isSelected={!area.disabled}
                    isDisabled={setAreaDisabled.isPending && pendingPath === area.path}
                    onChange={(isSelected) => void toggle(area.path, !isSelected)}
                    aria-label={area.disabled ? `Turn on ${area.label}` : `Turn off ${area.label}`}
                  />
                </Flex>
              </Flex>
            );
          })}
        </Flex>
      )}

      {staleDisabled.length > 0 && (
        <Flex direction="column" gap="xs">
          <Text size="xs" weight="medium" className="text-muted-foreground">
            No longer configured
          </Text>
          {staleDisabled.map((path) => (
            <SettingsRow key={path} title={areaLabel(path)} description={path}>
              <Button
                size="sm"
                variant="secondary"
                isDisabled={setAreaDisabled.isPending && pendingPath === path}
                onPress={() => void toggle(path, false)}
              >
                Turn back on
              </Button>
            </SettingsRow>
          ))}
        </Flex>
      )}

      {effectivePaths.length === 0 && staleDisabled.length === 0 && (
        <Text size="sm" className="text-muted-foreground">
          No areas — the whole suite runs as one
        </Text>
      )}

      <Text size="xs" className="text-muted-foreground text-pretty">
        Areas run in parallel and appear separately in results. Turning an area off removes it from
        future runs (shown as Disabled) without deleting it from the list above — turn it back on
        any time.
      </Text>
    </SettingsSection>
  );
}
